// The design's time scale (screens2.jsx GanttScreen, screens_views.jsx
// TaskTimeline), shared by the studio Schedule and a project's Timeline: the
// toolbar's "Timeline" kicker with Days/Weeks and the window, the header of day
// or week ticks, and the grid behind each row's track. Non-working days are
// the org's (org_settings.workdays), today is the org's; in Days today's column
// is lit, in Weeks a dashed line marks it. Type keeps the 11px floor, so a
// 60-day header drops the weekday the design squeezes in at 8px.
import type { CSSProperties, ReactNode } from 'react';
import { WEEKDAY_LABELS, fmtDayKey, monthAbbr } from '../lib/dates';
import { isWorkday, type Workdays } from '../lib/workdays';
import { RANGES, columnKeys, xOf, type TimeWindow, type Unit } from '../lib/timeline';

// Styles beside the controls they dress; nothing here holds state for Fast Refresh to lose.
// eslint-disable-next-line react-refresh/only-export-components
export const segment = (on: boolean, mono = false): CSSProperties => ({
  padding: mono ? '4px 9px' : '4px 10px',
  borderRadius: 5,
  border: 0,
  cursor: 'pointer',
  fontFamily: mono ? 'var(--hf-font-mono)' : 'inherit',
  fontSize: mono ? 'var(--hf-text-2xs)' : 'var(--hf-text-xs)',
  background: on ? 'var(--hf-surface-3)' : 'transparent',
  color: on ? 'var(--hf-ink)' : mono ? 'var(--hf-ink-3)' : 'var(--hf-ink-2)',
});
// eslint-disable-next-line react-refresh/only-export-components
export const segmentGroup: CSSProperties = {
  display: 'flex',
  gap: 2,
  padding: 3,
  borderRadius: 7,
  background: 'var(--hf-surface-2)',
  border: '1px solid var(--hf-rule-2)',
};
const kicker: CSSProperties = {
  fontSize: 'var(--hf-text-2xs)',
  color: 'var(--hf-ink-3)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};
// eslint-disable-next-line react-refresh/only-export-components
export const toolbar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  padding: '10px 16px',
  borderBottom: '1px solid var(--hf-rule)',
  background: 'var(--hf-surface)',
};

/** "Timeline", then Days/Weeks and the window's length, as the design's toolbar. */
export function WindowControls({
  unit,
  cols,
  onUnit,
  onCols,
}: {
  unit: Unit;
  cols: number;
  onUnit: (u: Unit) => void;
  onCols: (n: number) => void;
}) {
  return (
    <>
      <span style={{ ...kicker, marginRight: 4 }}>Timeline</span>
      <div role="group" aria-label="Show" style={segmentGroup}>
        {(['days', 'weeks'] as const).map((u) => (
          <button
            key={u}
            type="button"
            aria-pressed={unit === u}
            onClick={() => onUnit(u)}
            style={segment(unit === u)}
          >
            {u === 'days' ? 'Days' : 'Weeks'}
          </button>
        ))}
      </div>
      <div role="group" aria-label="Window" style={segmentGroup}>
        {RANGES[unit].map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={cols === r}
            onClick={() => onCols(r)}
            style={segment(cols === r, true)}
          >
            {unit === 'days' ? `${r} d` : `${r} wks`}
          </button>
        ))}
      </div>
    </>
  );
}

/** "30 day window", the design's note at the toolbar's end. */
export const WindowNote = ({ w }: { w: TimeWindow }) => (
  <span
    style={{
      fontSize: 'var(--hf-text-2xs)',
      color: 'var(--hf-ink-3)',
      fontFamily: 'var(--hf-font-mono)',
    }}
  >
    {w.cols} {w.unit === 'days' ? 'day' : 'week'} window
  </span>
);

/** The header row: the label cells the caller gives, then one tick per column. */
export function TimeHeader({
  w,
  today,
  workdays,
  columns,
  children,
}: {
  w: TimeWindow;
  today: string;
  workdays?: Workdays | null;
  /** The row's grid-template-columns; the ticks take the last track. */
  columns: string;
  /** The label column headings. */
  children: ReactNode;
}) {
  const keys = columnKeys(w);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: columns,
        padding: '0 16px',
        borderBottom: '1px solid var(--hf-rule)',
        background: 'var(--hf-surface)',
      }}
    >
      {children}
      <div style={{ display: 'flex', fontFamily: 'var(--hf-font-mono)', padding: '4px 0' }}>
        {keys.map((k, i) => {
          const d = new Date(`${k}T00:00:00`);
          if (w.unit === 'weeks')
            return (
              <span key={k} style={{ ...tick, color: 'var(--hf-ink-2)' }}>
                {fmtDayKey(k)}
              </span>
            );
          const now = k === today;
          const off = !isWorkday(k, workdays);
          const monthStart = d.getDate() === 1;
          return (
            <span
              key={k}
              style={{
                ...tick,
                flexDirection: 'column',
                gap: 1,
                borderRadius: 2,
                borderLeft: monthStart ? '1px solid var(--hf-accent)' : '1px solid transparent',
                background: now
                  ? 'var(--hf-accent-wash)'
                  : off
                    ? 'var(--hf-surface)'
                    : 'transparent',
                color: now ? 'var(--hf-accent-text)' : off ? 'var(--hf-ink-3)' : 'var(--hf-ink-2)',
                fontWeight: now ? 600 : 400,
              }}
            >
              {w.cols <= 30 && (
                <span style={{ color: now ? undefined : 'var(--hf-ink-3)', fontWeight: 400 }}>
                  {WEEKDAY_LABELS[d.getDay()]}
                </span>
              )}
              <span>{d.getDate()}</span>
              {(i === 0 || monthStart) && (
                <span style={{ color: 'var(--hf-accent-text)', fontWeight: 400 }}>
                  {monthAbbr(k)}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
const tick: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px 1px',
  fontSize: 'var(--hf-text-2xs)',
  lineHeight: 1.1,
};

/** The grid and today behind a row's track; absolutely placed, so put it first. */
export function TimeGrid({
  w,
  today,
  workdays,
}: {
  w: TimeWindow;
  today: string;
  workdays?: Workdays | null;
}) {
  const keys = columnKeys(w);
  // Weeks only: the middle of today.
  const at = xOf(today, w) + 0.5 / (w.cols * 7);
  return (
    <span aria-hidden style={{ position: 'absolute', inset: 0, display: 'flex' }}>
      {keys.map((k, i) => (
        <span
          key={k}
          style={{
            flex: 1,
            borderRight: i < keys.length - 1 ? '1px solid var(--hf-rule)' : 'none',
            background:
              w.unit === 'weeks'
                ? 'transparent'
                : k === today
                  ? 'var(--hf-accent-wash)'
                  : isWorkday(k, workdays)
                    ? 'transparent'
                    : 'var(--hf-surface)',
          }}
        />
      ))}
      {w.unit === 'weeks' && at > 0 && at < 1 && (
        <span
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${at * 100}%`,
            borderLeft: '1.5px dashed var(--hf-accent)',
          }}
        />
      )}
    </span>
  );
}
